import joi from 'joi';
import orderBy from 'lodash/orderBy';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  TextField,
  Theme,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  LevelAttributes,
  computeAmountUntilNextLevel,
} from '@loyalty/shared-common/gamification-api-client';
import { MembershipAttributes, ProfileAttributes } from '@loyalty/shared-common/profile-api-client';
import { QlfFormRow, QlfNumberField, useAlerts } from '@qualifio/ui-commons';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { getLocalizedProperty } from '@loyalty/shared-common';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { LevelImage } from '../../../shared/level/LevelImage';
import { Title } from '../../../shared/layout/Title';
import { useApi } from '../../../shared/api-client/ApiClientProvider';
import { useJoiResolver } from '../../../shared/forms/use-joi-resolver';
import { useLevels } from '../../../levels/LevelsContext';
import { useLevelsConfig } from '../../../levels/LevelsConfigContext';
import { useProgram } from '../../../loyalty/ProgramContext';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  dialogTitle: {
    padding: spacing(4, 8, 1),
    textAlign: 'center',
  },
  dialogContent: {
    paddingTop: spacing(2),
    paddingBottom: spacing(1),
  },
  dialogActions: {
    padding: spacing(1, 8, 4),
    justifyContent: 'center',
  },
  levelIcon: {
    marginLeft: spacing(0.5),
    marginRight: '5px',
    width: '30px !important',
    height: '30px !important',
  },
  progression: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'left',
    height: '50px',
  },
  progressionInput: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  newProgression: {
    paddingRight: '10% !important',
  },
  chip: {
    background: 'inherit !important',
    transition: 'none !important',
  },
  levelSelection: {
    textAlign: 'left',
    paddingRight: '10% !important',
  },
}));

export interface AdjustLevelDialogProps {
  profile: ProfileAttributes;
  membership: MembershipAttributes;
  onClose: () => void;
  onApply?: () => Promise<void> | void;
}

interface FormValue {
  levelId: string | null;
  progression?: number;
}

const adjustLevelFormSchema = joi.object<FormValue>({
  levelId: joi
    .string()
    .uuid()
    .allow('null')
    .custom(value => (value === 'null' ? null : value)),
  progression: joi.number().optional(),
});

export function AdjustLevelDialog({
  profile,
  membership,
  onClose,
  onApply,
}: AdjustLevelDialogProps) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { notify } = useAlerts();
  const [applying, setApplying] = useState(false);
  const [newNextLevel, setNewNextLevel] = useState<LevelAttributes>();
  const [selectedLevel, setSelectedLevel] = useState<LevelAttributes>();
  const { program } = useProgram();
  const { levelsConfig } = useLevelsConfig();
  const { levels } = useLevels();
  const { profileApi } = useApi();
  const currentLevel = levels.find(level => level.id === membership?.level?.current);
  const sortedLevels = orderBy(levels, ['minCurrencyAmount']);
  const nextLevel = sortedLevels.find(
    l => !currentLevel || l.minCurrencyAmount > (currentLevel.minCurrencyAmount ?? 0),
  );
  const levelCurrencyBalance = profile.currencies?.find(
    currencyBalance => currencyBalance.currencyId === levelsConfig?.currencyId,
  );

  const { current: amountTowardsNextLevel, total: totalForNextLevel } = computeAmountUntilNextLevel(
    currentLevel,
    nextLevel,
    levelCurrencyBalance,
  );

  const resolver = useJoiResolver(adjustLevelFormSchema);
  const {
    watch,
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<FormValue>({
    resolver,
  });
  const selectedLevelId = watch('levelId');
  const selectedProgression = watch('progression');

  const handleApply = useCallback(async () => {
    setApplying(true);
    try {
      await profileApi.updateMemberLevel(program.id, profile.id, {
        levelsConfig,
        levels,
        forcedLevel: selectedLevel || null,
        forcedProgression:
          (selectedLevel?.minCurrencyAmount || 0) + parseInt(`${selectedProgression}`, 10),
      });
      notify(t('profiles.level.adjust.apply.success', { member: profile.firstName }), 'success');
      await onApply?.();
      onClose();
    } catch (error) {
      console.error(error);
      notify(t('profiles.currency-balances.adjust.apply.error', { error }), 'error');
    } finally {
      setApplying(false);
    }
  }, [levels, levelsConfig, profile.id, program, selectedLevel, selectedProgression]);

  useEffect(() => {
    const tempNewNextLevel = sortedLevels.find(
      l => !selectedLevel || l.minCurrencyAmount > (selectedLevel.minCurrencyAmount ?? 0),
    );
    setNewNextLevel(tempNewNextLevel);
  }, [levels, selectedLevel]);

  useEffect(() => {
    const tempSelectedLevel = levels.find(level => level.id === selectedLevelId) || undefined;
    setSelectedLevel(tempSelectedLevel);
  }, [levels, selectedLevelId]);

  const newMaximumProgression = newNextLevel
    ? newNextLevel.minCurrencyAmount - (selectedLevel?.minCurrencyAmount || 0)
    : 0;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <div>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 0,
          }}
        >
          <FontAwesomeIcon icon={faTimes} />
        </IconButton>
        <Title className={classes.dialogTitle}>
          {t('profiles.level.adjust.title', { firstName: profile.firstName })}
        </Title>

        <form onSubmit={handleSubmit(handleApply, console.error)}>
          <DialogContent className={classes.dialogContent}>
            <QlfFormRow label={t('profiles.level.adjust.current.label')}>
              {currentLevel ? (
                <div className={classes.progression}>
                  <LevelImage level={currentLevel} className={classes.levelIcon} />
                  {getLocalizedProperty(currentLevel, 'name', i18n.language)}
                </div>
              ) : (
                <Box className={classes.placeholder}>{t('profiles.details.level.placeholder')}</Box>
              )}
            </QlfFormRow>

            <QlfFormRow label={t('profiles.level.adjust.progression.label')}>
              {nextLevel ? (
                <div className={classes.progression}>
                  {amountTowardsNextLevel}/{totalForNextLevel}
                  <LevelImage level={nextLevel} className={classes.levelIcon} />
                  {getLocalizedProperty(nextLevel, 'name', i18n.language)}
                </div>
              ) : (
                <div className={classes.progression}>
                  {t('profiles.level.adjust.progression.max')}
                </div>
              )}
            </QlfFormRow>

            <QlfFormRow label={t('profiles.level.adjust.desired.level')}>
              <Controller
                control={control}
                name="levelId"
                defaultValue={currentLevel?.id || 'null'}
                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    select
                    inputRef={ref}
                    error={!!error}
                    helperText={error?.message}
                    className={classes.levelSelection}
                  >
                    <MenuItem key={'null'} value={'null'}>
                      <Chip
                        key={'null'}
                        label={t('profiles.level.adjust.no-level')}
                        className={classes.chip}
                      />
                    </MenuItem>
                    {sortedLevels.map(level => (
                      <MenuItem key={level.id} value={level.id}>
                        <Chip
                          key={level.id}
                          label={getLocalizedProperty(level, 'name', i18n.language)}
                          className={classes.chip}
                          avatar={<img src={getLocalizedProperty(level, 'image', i18n.language)} />}
                        />
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </QlfFormRow>

            <QlfFormRow label={t('profiles.level.adjust.desired.progression')}>
              {newNextLevel ? (
                <Controller
                  control={control}
                  name="progression"
                  defaultValue={0}
                  render={({ field: { ref, ...field }, fieldState: { error } }) => (
                    <div className={classes.progressionInput}>
                      <QlfNumberField
                        {...field}
                        inputRef={ref}
                        min={0}
                        max={newMaximumProgression > 0 ? newMaximumProgression - 1 : 0}
                        error={!!error}
                        helperText={error?.message}
                        className={classes.newProgression}
                        InputProps={{
                          endAdornment: (
                            <div className={classes.progression}>
                              /{newMaximumProgression}
                              <LevelImage level={newNextLevel} className={classes.levelIcon} />
                              {getLocalizedProperty(newNextLevel, 'name', i18n.language)}
                            </div>
                          ),
                        }}
                      />
                    </div>
                  )}
                />
              ) : (
                <div className={classes.progression}>
                  {t('profiles.level.adjust.progression.max')}
                </div>
              )}
            </QlfFormRow>
          </DialogContent>

          <DialogActions className={classes.dialogActions}>
            <Button color="grey" onClick={onClose}>
              {t('generic.actions.cancel')}
            </Button>
            <Button
              color="success"
              type="submit"
              startIcon={applying && <FontAwesomeIcon icon={faSpinner} spin />}
              disabled={applying || !isDirty}
              autoFocus
            >
              {t('profiles.currency-balances.adjust.apply.label')}
            </Button>
          </DialogActions>
        </form>
      </div>
    </Dialog>
  );
}
